import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Users, Shield, Truck } from "lucide-react";
import { useAuth } from "@/context/AuthProvider";
import { API } from "@/config";
import { ThemedPhoneInput } from "@/components/ui/ThemedPhoneInput";

/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */
export const startGoogleLogin = () => {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export const LoginPage = () => {
  const { user, login, signup } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const roleParam = new URLSearchParams(loc.search).get("role") || "customer";
  // "login" | "signup" | "forgot" — controls which panel renders inside the card.
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [signupAddress, setSignupAddress] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  useEffect(() => {
    if (user && user.role) {
      const home = { admin: "/admin", employee: "/employee", customer: "/account" }[user.role] || "/";
      nav(home, { replace: true });
    }
  }, [user, nav]);

  const roleMeta = {
    customer: { title: "Customer Portal",  icon: Users,  blurb: "Track, edit or cancel your bookings.", allowSignup: true,  allowGoogle: true  },
    employee: { title: "Employee Portal",  icon: Truck,  blurb: "Delivery driver portal — pick sheets & labels.", allowSignup: false, allowGoogle: false },
    admin:    { title: "Admin Back Office", icon: Shield, blurb: "Full inventory + reservation management.", allowSignup: false, allowGoogle: false },
  }[roleParam] || {};
  const RoleIcon = roleMeta.icon || Users;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "signup" && roleMeta.allowSignup) {
        if (!firstName.trim() || !lastName.trim()) {
          toast.error("Please enter both your first and last name.");
          setBusy(false); return;
        }
        const displayName = `${firstName.trim()} ${lastName.trim()}`;
        const data = await signup(email, password, displayName, phone, signupAddress, firstName.trim(), lastName.trim());
        toast.success(`Welcome, ${data.name}!`);
      } else {
        const data = await login(email, password);
        if (roleParam !== "customer" && data.role !== roleParam) {
          toast.error(`That account is a ${data.role} — please choose the ${data.role} tab.`);
        } else if (roleParam === "customer" && data.role !== "customer") {
          toast.error(`This is a ${data.role} account — use the ${data.role} tab.`);
        } else {
          toast.success(`Welcome back, ${data.name || data.email}!`);
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed. Please try again.");
    } finally { setBusy(false); }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("Enter your account email."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setForgotSent(true);
    } catch (err) {
      // Endpoint is enumeration-safe — should always 200. Still swallow errors gracefully.
      setForgotSent(true);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden py-16 px-4">
      <div className="glass max-w-md w-full p-8 md:p-10 relative z-10" data-testid="login-page">
        <button onClick={() => nav("/")} className="text-[#B9C8D5] hover:text-[#FFD166] text-sm flex items-center gap-1 mb-6" data-testid="login-back">
          <ArrowLeft className="w-4 h-4" /> Back to site
        </button>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-[#FFD166]/15 border border-[#FFD166]/40 flex items-center justify-center">
            <RoleIcon className="w-5 h-5 text-[#FFD166]" />
          </div>
          <div>
            <p className="eyebrow text-[#FFD166]">
              {mode === "signup" ? "Create account" : mode === "forgot" ? "Reset password" : "Sign in"}
            </p>
            <h1 className="font-serif text-3xl text-[#F6F1E4]">{roleMeta.title}</h1>
          </div>
        </div>
        <p className="text-sm text-[#B9C8D5] mt-3">{roleMeta.blurb}</p>

        {mode !== "forgot" && (
          <div className="mt-6 grid grid-cols-3 gap-1 p-1 rounded-lg bg-black/30 border border-[#FFD166]/20 text-xs" data-testid="login-role-tabs">
            {["customer", "employee", "admin"].map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => { nav(`/login?role=${r}`); setMode("login"); }}
                data-testid={`login-role-${r}`}
                className={`py-2 rounded-md transition-colors capitalize ${roleParam === r ? "bg-[#FFD166] text-[#040A0F]" : "text-[#F6F1E4]/70 hover:text-[#FFD166]"}`}
              >{r}</button>
            ))}
          </div>
        )}

        {/* ---------- FORGOT PASSWORD PANEL ---------- */}
        {mode === "forgot" ? (
          <div className="mt-6" data-testid="forgot-password-panel">
            {forgotSent ? (
              <div className="p-5 rounded-xl border border-emerald-500/40 bg-emerald-500/5 text-sm text-[#F6F1E4]" data-testid="forgot-password-sent">
                <p>If an account exists for <strong className="text-[#FFD166]">{email}</strong>, we've sent a reset link.</p>
                <p className="mt-2 text-[#B9C8D5]">Check your inbox (and spam folder) in a minute. The link expires in 2 hours.</p>
                <button type="button" onClick={() => { setMode("login"); setForgotSent(false); }} className="btn-ghost mt-4 w-full" data-testid="forgot-back-to-login">
                  Back to sign in
                </button>
              </div>
            ) : (
              <form onSubmit={submitForgot} className="space-y-4">
                <p className="text-sm text-[#B9C8D5]">Enter your account email and we'll send you a link to pick a new password.</p>
                <input required type="email" className="input" placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" />
                <button type="submit" disabled={busy} className="w-full py-3 rounded-full bg-gradient-to-r from-[#D97B37] to-[#F4A93A] text-[#1a0a03] font-medium disabled:opacity-60 shadow-[0_4px_20px_rgba(217,123,55,0.35)]" data-testid="forgot-submit">
                  {busy ? "Sending…" : "Send reset link"}
                </button>
                <button type="button" onClick={() => setMode("login")} className="w-full text-sm text-[#B9C8D5] hover:text-[#FFD166]" data-testid="forgot-cancel">
                  ← Back to sign in
                </button>
              </form>
            )}
          </div>
        ) : (
          <>
            <form onSubmit={submit} className="mt-6 space-y-4">
              {mode === "signup" && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <input required className="input" placeholder="First name *" value={firstName} onChange={(e) => setFirstName(e.target.value)} data-testid="signup-first-name" />
                    <input required className="input" placeholder="Last name *" value={lastName} onChange={(e) => setLastName(e.target.value)} data-testid="signup-last-name" />
                  </div>
                  <ThemedPhoneInput
                    value={phone}
                    onChange={setPhone}
                    required
                    placeholder="Phone number (for delivery updates)"
                    data-testid="signup-phone"
                  />
                  <input className="input" placeholder="Home / mailing address (optional — speeds up booking)" value={signupAddress} onChange={(e) => setSignupAddress(e.target.value)} data-testid="signup-address" />
                </>
              )}
              <input required type="email" className="input" placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" />
              <input required type="password" minLength={8} className="input" placeholder="Password (8+ chars)" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" />
              <button type="submit" disabled={busy} data-testid="login-submit" className="w-full py-3 rounded-full bg-gradient-to-r from-[#D97B37] to-[#F4A93A] text-[#1a0a03] font-medium disabled:opacity-60 shadow-[0_4px_20px_rgba(217,123,55,0.35)]">
                {busy ? "…" : (mode === "signup" ? "Create account" : "Sign in")}
              </button>

              {mode === "login" && (
                <div className="text-right">
                  <button type="button" onClick={() => { setMode("forgot"); setForgotSent(false); }} className="text-xs text-[#FFD166] hover:underline" data-testid="forgot-password-link">
                    Forgot your password?
                  </button>
                </div>
              )}
            </form>

            {roleMeta.allowGoogle && (
              <>
                <div className="my-6 flex items-center gap-3 text-[10px] uppercase tracking-[0.22em] text-[#B9C8D5]">
                  <div className="flex-1 h-px bg-[#FFD166]/20" /> or <div className="flex-1 h-px bg-[#FFD166]/20" />
                </div>
                <button type="button" onClick={startGoogleLogin} data-testid="login-google" className="w-full py-3 rounded-full bg-white text-[#1a1a1a] font-medium flex items-center justify-center gap-3 hover:brightness-95 transition-all">
                  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden><path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.44c-.28 1.44-1.12 2.66-2.39 3.48v2.89h3.86c2.26-2.09 3.58-5.17 3.58-8.61z"/><path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.94-2.92l-3.86-2.89c-1.07.72-2.44 1.15-4.08 1.15-3.13 0-5.79-2.11-6.74-4.96H1.28v3.12A11.998 11.998 0 0 0 12 24z"/><path fill="#FBBC05" d="M5.26 14.38c-.24-.72-.38-1.49-.38-2.28s.14-1.56.38-2.28V6.7H1.28C.46 8.33 0 10.11 0 12s.46 3.67 1.28 5.3l3.98-2.92z"/><path fill="#EA4335" d="M12 4.76c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.94 1.19 15.23 0 12 0 7.31 0 3.26 2.7 1.28 6.7l3.98 3.12C6.21 6.87 8.87 4.76 12 4.76z"/></svg>
                  Continue with Google
                </button>
              </>
            )}

            {roleMeta.allowSignup && (
              <p className="text-sm text-[#B9C8D5] text-center mt-6">
                {mode === "login" ? (
                  <>New here? <button type="button" onClick={() => setMode("signup")} className="text-[#FFD166] hover:underline" data-testid="switch-to-signup">Create an account</button></>
                ) : (
                  <>Have an account? <button type="button" onClick={() => setMode("login")} className="text-[#FFD166] hover:underline" data-testid="switch-to-login">Sign in</button></>
                )}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};
